import React from 'react';
import { db } from '@repo/database';
import { convertToDecimal } from '@repo/money-formatter';
import type { Block } from '@/cms/types';

interface PlatformMetricsData {
  numProjects: number;
  numJars: number;
  jarsAccumulated: bigint;
}

function formatNumber(number: number): string {
  if (Math.abs(number) >= 1000000000) {
    const result = (number / 1000000000).toFixed(1);

    return result.endsWith('.0') ? `${result.slice(0, -2)}B` : `${result}B`;
  } else if (Math.abs(number) >= 1000000) {
    const result = (number / 1000000).toFixed(1);

    return result.endsWith('.0') ? `${result.slice(0, -2)}M` : `${result}M`;
  } else if (Math.abs(number) >= 1000) {
    const result = (number / 1000).toFixed(1);

    return result.endsWith('.0') ? `${result.slice(0, -2)}K` : `${result}K`;
  }

  return number.toString();
}

export async function PlatformMetrics(
  data: Block & {
    title: string;
    projectsLabel: string;
    jarsLabel: string;
    totalMoneyLabel: string;
  },
): Promise<React.JSX.Element> {
  const { title, projectsLabel, jarsLabel, totalMoneyLabel } = data;

  let platformStats = {
    numProjects: 999,
    numJars: 99999,
    jarsAccumulated: BigInt(9876543210),
  };

  if (typeof window === 'undefined') {
    const result: PlatformMetricsData[] = await db.$queryRaw`
        SELECT (SELECT COUNT(*) FROM "Project")::int                       AS "numProjects",
               (SELECT COUNT(*) FROM "Jar")::int                           AS "numJars",
               (SELECT COALESCE(SUM("accumulated"), 0) FROM "Jar")::bigint AS "jarsAccumulated";
    `;

    platformStats = result[0];
  }

  return (
    <div className='container max-w-[812px] py-16'>
      <div className='text-center'>
        <h2 className='mb-10 md:mb-16 text-4xl font-extrabold'>{title}</h2>
      </div>

      <div className='row-gap-8 grid grid-cols-1 md:grid-cols-3 text-gray-400'>
        <div className='mb-12 text-center md:mb-0 md:border-r-2'>
          <div className='font-heading text-6xl font-bold text-gray-300'>
            {formatNumber(platformStats.numProjects)}
          </div>
          <p className='text-sm font-medium uppercase tracking-widest md:text-base'>
            {projectsLabel}
          </p>
        </div>

        <div className='mb-12 text-center md:mb-0 md:border-r-2'>
          <div className='font-heading text-6xl font-bold text-gray-300'>
            {formatNumber(platformStats.numJars)}
          </div>
          <p className='text-sm font-medium uppercase tracking-widest md:text-base'>
            {jarsLabel}
          </p>
        </div>

        <div className='text-center'>
          <div className='font-heading text-6xl font-bold text-gray-300'>
            {formatNumber(convertToDecimal(platformStats.jarsAccumulated))}
          </div>
          <p className='text-sm font-medium uppercase tracking-widest md:text-base'>
            {totalMoneyLabel}
          </p>
        </div>
      </div>
    </div>
  );
}
